import { t } from "@lingui/macro";
import {
  BulkDeleteTimeTrackingItemsDocument,
  type BulkDeleteTimeTrackingItemsMutation,
  type BulkDeleteTimeTrackingItemsMutationVariables,
  CheckTimeTrackingItemsLockedDocument,
  type CheckTimeTrackingItemsLockedQuery,
  type CheckTimeTrackingItemsLockedQueryVariables,
  GetOurWorkBudgetItemsSimpleMapOptionsDocument,
  type GetOurWorkBudgetItemsSimpleMapOptionsQuery,
  type GetOurWorkBudgetItemsSimpleMapOptionsQueryVariables,
  GetTasksSimpleMapOptionsDocument,
  type GetTasksSimpleMapOptionsQuery,
  type GetTasksSimpleMapOptionsQueryVariables,
  type SortOrder,
  SqlOperator,
  TimeTrackingBillingStatusEnum,
  type TimeTrackingItemOrderByColumnEnum,
  TimeTrackingReportGroupBy,
  TimeTrackingReportGroupedByDocument,
  type TimeTrackingReportGroupedByQuery,
  type TimeTrackingReportGroupedByQueryVariables,
  type TimeTrackingReportItemFragment,
  TimeTrackingReportOptionsDocument,
  type TimeTrackingReportOptionsQuery,
  type TimeTrackingReportOptionsQueryVariables,
  TimeTrackingReportSelectedSummaryDocument,
  type TimeTrackingReportSelectedSummaryQuery,
  type TimeTrackingReportSelectedSummaryQueryVariables,
  TimeTrackingReportSummaryDocument,
  type TimeTrackingReportSummaryQuery,
  type TimeTrackingReportSummaryQueryVariables,
  TimeTrackingReportsWhereColumn,
  TimeTrackingUngroupedItemsDocument,
  type TimeTrackingUngroupedItemsQuery,
  type TimeTrackingUngroupedItemsQueryVariables,
  UnlockTimeTrackingItemsDocument,
  type UnlockTimeTrackingItemsMutation,
  type UnlockTimeTrackingItemsMutationVariables,
  UpdateTimeTrackingItemsBillableDocument,
  type UpdateTimeTrackingItemsBillableMutation,
  type UpdateTimeTrackingItemsBillableMutationVariables,
  UserScopeEnum,
  UsersWhereColumn,
} from "@src/__generated__/graphql";
import { getBillingCategoryOptions } from "@src/components/modules/projects/list/Store";
import { ProjectCategorySelectBottomExtraContent } from "@src/components/ui-kit/ProjectCategorySelect";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import {
  commonQueryVariables,
  commonSearchParams,
} from "@src/utils/apolloHelpers";
import { getBooleanFilterOptions } from "@src/utils/components/filters/constants";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { can } from "@src/utils/components/permissions";
import { OrderBy } from "@src/utils/components/sorting/OrderBy";
import { toApiDate } from "@src/utils/dates";
import { formatDate } from "@src/utils/formatters";
import mapToOptions from "@src/utils/map-to-options";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import type { DateRange, Prettify } from "@src/utils/types";
import { saveQuery } from "@src/utils/url";
import { endOfMonth, startOfMonth } from "date-fns";
import { clone, type Omit } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import router from "next/router";

type TimeTrackingReportGroupedByItem =
  TimeTrackingReportGroupedByQuery["timeTrackingReport"]["data"][0];

type GroupedByValue = TimeTrackingReportGroupBy | "none";

export const TIME_TRACKING_REPORT_SUBTASK_FILTER_COLUMN =
  "SUBTASK_ID" as TimeTrackingReportsWhereColumn;

export type TimeTrackingRow = Prettify<
  {
    id: string;
    __subRows: TimeTrackingReportItemFragment[];
  } & Omit<TimeTrackingReportGroupedByItem, "items">
>;

export class Store {
  appStore: AppStore;

  @observable.ref
  selectedIds: string[] = [];

  @observable.ref
  selectedSubtaskIds: string[] = [];

  @observable.ref
  summary:
    | TimeTrackingReportSummaryQuery["getTimeTrackingItemsSummary"]
    | undefined = undefined;

  @observable.ref
  groupedReports: TimeTrackingRow[] = [];

  @observable.ref
  ungroupedItems: TimeTrackingReportItemFragment[] = [];

  private groupedPagination = new PaginationState("timeTrackingReport", {
    onChangePagination: () => {
      if (!this.isInitialized) return;
      this.updateUrl();
      this.refetch();
    },
  });

  private ungroupedPagination = new PaginationState("timeTrackingReport", {
    onChangePagination: () => {
      if (!this.isInitialized) return;
      this.updateUrl();
      this.refetch();
    },
    perPageOptions: [50, 100, 250, 500],
  });

  private isInitialized = false;

  private updateUrl() {
    const query = { ...commonSearchParams(this), groupBy: this.groupBy };
    router.replace({ query }, undefined, { shallow: true });
    saveQuery(query);
  }

  @action.bound
  refetch() {
    if (this.isGroupedByNone) {
      this.fetchUngroupeditems();
      return;
    }

    this.fetchReport();
  }

  private reportFetcher = new FetchHelper<
    TimeTrackingReportGroupedByQuery,
    TimeTrackingReportGroupedByQueryVariables
  >(TimeTrackingReportGroupedByDocument);

  private ungroupedItemsFetcher = new FetchHelper<
    TimeTrackingUngroupedItemsQuery,
    TimeTrackingUngroupedItemsQueryVariables
  >(TimeTrackingUngroupedItemsDocument);

  private summaryFetcher = new FetchHelper<
    TimeTrackingReportSummaryQuery,
    TimeTrackingReportSummaryQueryVariables
  >(TimeTrackingReportSummaryDocument);

  private selectedSummaryFetcher = new FetchHelper<
    TimeTrackingReportSelectedSummaryQuery,
    TimeTrackingReportSelectedSummaryQueryVariables
  >(TimeTrackingReportSelectedSummaryDocument);

  private optionsFetcher = new FetchHelper<
    TimeTrackingReportOptionsQuery,
    TimeTrackingReportOptionsQueryVariables
  >(TimeTrackingReportOptionsDocument);

  private owbiOptionsFetcher = new FetchHelper<
    GetOurWorkBudgetItemsSimpleMapOptionsQuery,
    GetOurWorkBudgetItemsSimpleMapOptionsQueryVariables
  >(GetOurWorkBudgetItemsSimpleMapOptionsDocument);

  private tasksOptionsFetcher = new FetchHelper<
    GetTasksSimpleMapOptionsQuery,
    GetTasksSimpleMapOptionsQueryVariables
  >(GetTasksSimpleMapOptionsDocument);

  private checkLockedFetcher = new FetchHelper<
    CheckTimeTrackingItemsLockedQuery,
    CheckTimeTrackingItemsLockedQueryVariables
  >(CheckTimeTrackingItemsLockedDocument);

  private unlockMutator = new MutationHelper<
    UnlockTimeTrackingItemsMutation,
    UnlockTimeTrackingItemsMutationVariables
  >(UnlockTimeTrackingItemsDocument);

  private deleteMutator = new MutationHelper<
    BulkDeleteTimeTrackingItemsMutation,
    BulkDeleteTimeTrackingItemsMutationVariables
  >(BulkDeleteTimeTrackingItemsDocument);

  private updateBillableMutator = new MutationHelper<
    UpdateTimeTrackingItemsBillableMutation,
    UpdateTimeTrackingItemsBillableMutationVariables
  >(UpdateTimeTrackingItemsBillableDocument);

  @observable.ref
  groupBy: GroupedByValue = "none";

  orderBy = new OrderBy<TimeTrackingItemOrderByColumnEnum>([]);

  @observable.ref
  includeArchivedOptions = true;

  @observable.ref
  optionsData?: {
    clientsSimpleMap?: TimeTrackingReportOptionsQuery["clientsSimpleMap"];
    projectsSimpleMap?: TimeTrackingReportOptionsQuery["projectsSimpleMap"];
    timeTrackingWorkTypes?: TimeTrackingReportOptionsQuery["timeTrackingWorkTypes"];
    teamSimpleMap?: TimeTrackingReportOptionsQuery["teamSimpleMap"];
    userSimpleMap?: TimeTrackingReportOptionsQuery["userSimpleMap"];
    tasksSimpleMap?: GetTasksSimpleMapOptionsQuery["tasksSimpleMap"];
    subtasksSimpleMap?: GetTasksSimpleMapOptionsQuery["tasksSimpleMap"];
    ourWorkBudgetItemsSimpleMap?: GetOurWorkBudgetItemsSimpleMapOptionsQuery["ourWorkBudgetItemsSimpleMap"];
    projectCategories?: TimeTrackingReportOptionsQuery["projectCategories"];
  };

  where = new Filters<TimeTrackingReportsWhereColumn>(
    [
      new Filter({
        column: TimeTrackingReportsWhereColumn.TrackedForDate,
        operator: SqlOperator.Between,
        hidden: true,
        dateRange: true,
        value: [
          toApiDate(startOfMonth(new Date())),
          toApiDate(endOfMonth(new Date())),
        ],
        options: [],
      }),
      new Filter({
        title: t`Clients`,
        column: TimeTrackingReportsWhereColumn.ClientId,
        operator: SqlOperator.In,
        options: [],
        onChange: () => {
          this.filterBrandOptions();
          this.filterProjectOptions();
        },
      }),
      new Filter({
        title: t`Brands`,
        column: TimeTrackingReportsWhereColumn.BrandId,
        operator: SqlOperator.In,
        options: [],
        onChange: () => {
          this.filterProjectOptions();
        },
      }),
      new Filter({
        title: t`Projects`,
        column: TimeTrackingReportsWhereColumn.ProjectId,
        operator: SqlOperator.In,
        options: [],
        onChange: () => {
          this.fetchOWBIOptions();
        },
      }),
      new Filter({
        title: t`Project categories`,
        column: TimeTrackingReportsWhereColumn.ProjectCategoryId,
        operator: SqlOperator.In,
        options: [],
        bottomExtraContent: <ProjectCategorySelectBottomExtraContent />,
      }),
      new Filter({
        title: t`Budget items`,
        column: TimeTrackingReportsWhereColumn.OurWorkBudgetItemId,
        operator: SqlOperator.In,
        noOptionsMessage: t`Please select Project first`,
        options: [],
        onChange: () => {
          this.fetchTasksOptions();
        },
      }),
      new Filter({
        title: t`Tasks`,
        noOptionsMessage: t`Please select Budget item first`,
        column: TimeTrackingReportsWhereColumn.TaskId,
        operator: SqlOperator.In,
        options: [],
        onChange: () => {
          this.fetchSubtasksOptions();
        },
        mutateToGql: (value) => {
          return this.selectedSubtaskIds.length
            ? this.selectedSubtaskIds
            : value;
        },
      }),
      new Filter({
        title: t`Subtasks`,
        noOptionsMessage: t`Please select Task first`,
        column: TIME_TRACKING_REPORT_SUBTASK_FILTER_COLUMN,
        operator: SqlOperator.In,
        includeInGql: false,
        onChange: (value) => {
          this.setSelectedSubtaskIds(value ?? []);
        },
        options: [],
      }),
      new Filter({
        title: t`Positions`,
        column: TimeTrackingReportsWhereColumn.WorkTypeId,
        operator: SqlOperator.In,
        options: [],
      }),
      new Filter({
        title: t`Teams`,
        column: TimeTrackingReportsWhereColumn.TeamId,
        operator: SqlOperator.In,
        options: [],
        onChange: () => {
          this.filterUserOptions();
        },
      }),
      new Filter({
        title: t`Billing category`,
        column: TimeTrackingReportsWhereColumn.Billable,
        operator: SqlOperator.Eq,
        options: getBillingCategoryOptions(),
      }),
      new Filter({
        title: t`Billing status`,
        column: TimeTrackingReportsWhereColumn.BillingStatus,
        operator: SqlOperator.In,
        options: [
          {
            label: t`Invoiced`,
            value: TimeTrackingBillingStatusEnum.Invoiced,
          },
          {
            label: t`Uninvoiced`,
            value: TimeTrackingBillingStatusEnum.Uninvoiced,
          },
          {
            label: t`Overserviced`,
            value: TimeTrackingBillingStatusEnum.Overserviced,
          },
        ],
      }),
      new Filter({
        title: t`Users`,
        column: TimeTrackingReportsWhereColumn.UserId,
        operator: SqlOperator.In,
        options: [],
        mutateToGql: (val, filter) => {
          if (val[0] === "work-expense") {
            filter.operator = SqlOperator.IsNull;
          } else {
            filter.operator = SqlOperator.In;
          }
          return val;
        },
      }),
      new Filter({
        title: t`Status`,
        column: TimeTrackingReportsWhereColumn.Status,
        operator: SqlOperator.In,
        options: [
          {
            label: t`Active`,
            value: "active",
          },
          {
            label: t`Archived`,
            value: "archived",
          },
          {
            label: t`Finished`,
            value: "finished",
          },
        ],
        onChange: () => {
          this.filterProjectOptions();
          this.filterUserOptions();
        },
      }),
      new Filter({
        title: t`Has time billing`,
        column: TimeTrackingReportsWhereColumn.HasTimeBilling,
        options: getBooleanFilterOptions(),
        value: [],
        operator: SqlOperator.Eq,
      }),
      new Filter({
        column: TimeTrackingReportsWhereColumn.Id,
        operator: SqlOperator.In,
        hidden: true,
        includeInGql: false,
        options: [],
      }),
    ],
    {
      onChange: () => {
        this.pagination.resetPage();
        this.fetchSummary();
      },
      tableKey: "time-tracking-report",
      orderBy: this.orderBy,
    },
  );

  constructor(appStore: AppStore) {
    makeObservable(this);
    this.appStore = appStore;
    this.initFilters();
  }

  @action.bound
  async initFilters() {
    if (router.query.groupBy) {
      this.groupBy = router.query.groupBy as GroupedByValue;
    }
    this.where.fromURLSearchParam(router.query, { skipOnChange: true });
    this.setSelectedSubtaskIds(this.subtaskFilter?.value ?? []);
    this.pagination.fromURLSearchParam(router.query);
    this.orderBy.fromURLSearchParam(router.query);
    await this.fetchOptionsData();
    await this.fetchOWBIOptions();
    await this.fetchTasksOptions();
    this.isInitialized = true;

    this.fetchSummary();

    this.refetch();
  }

  @computed
  get isGroupedByNone(): boolean {
    return this.groupBy === "none";
  }

  @computed
  get pagination() {
    return this.isGroupedByNone
      ? this.ungroupedPagination
      : this.groupedPagination;
  }

  @observable.ref
  selectedSummary?:
    | TimeTrackingReportSelectedSummaryQuery["getTimeTrackingItemsSummary"]
    | undefined = undefined;

  @observable.ref
  lockedSelectedIds: string[] = [];

  @observable
  isSummaryLoading = false;

  @computed
  get hasLockedSelected(): boolean {
    return this.lockedSelectedIds.length > 0;
  }

  @computed
  private get idFilter(): Filter<TimeTrackingReportsWhereColumn> | undefined {
    return this.where.filtersByColumn.get(TimeTrackingReportsWhereColumn.Id);
  }

  get groupByOptions(): Array<{ value: GroupedByValue; label: string }> {
    return [
      { value: "none", label: t`None` },
      { value: TimeTrackingReportGroupBy.Date, label: t`Date` },
      { value: TimeTrackingReportGroupBy.Brand, label: t`Brand` },
      { value: TimeTrackingReportGroupBy.Project, label: t`Project` },
      { value: TimeTrackingReportGroupBy.BudgetItem, label: t`Budget Item` },
      { value: TimeTrackingReportGroupBy.Task, label: t`Task` },
      { value: TimeTrackingReportGroupBy.User, label: t`User` },
      { value: TimeTrackingReportGroupBy.WorkType, label: t`Position` },
    ];
  }

  @computed
  get queryParams() {
    return commonQueryVariables(this);
  }

  @computed
  get dateRangeFilter() {
    return this.where.filtersByColumn.get(
      TimeTrackingReportsWhereColumn.TrackedForDate,
    );
  }

  @computed
  get dateRangeValue(): DateRange {
    const values = this.dateRangeFilter?.value ?? [];
    return {
      start: new Date(values[0]),
      end: new Date(values[1]),
    };
  }

  @computed
  get dateRangeReadable(): string {
    return `${formatDate(this.dateRangeValue.start)} - ${formatDate(this.dateRangeValue.end)}`;
  }

  @computed
  get activeGroupByLabel(): string | undefined {
    return this.groupByOptions.find(({ value }) => value === this.groupBy)
      ?.label;
  }

  @computed
  get readPermission() {
    if (can("timeTracking_report_read_all")) return "all";
    if (can("timeTracking_report_read_team")) return "team";
    return "own";
  }

  @computed
  get isReportLoading(): boolean {
    return (
      this.reportFetcher.isLoading.value ||
      this.ungroupedItemsFetcher.isLoading.value
    );
  }

  @computed
  get areFiltersLoading(): boolean {
    return (
      this.optionsFetcher.isLoading.value ||
      this.owbiOptionsFetcher.isLoading.value ||
      this.tasksOptionsFetcher.isLoading.value
    );
  }

  @action
  setGroupBy(groupBy: GroupedByValue): void {
    if (this.groupBy === groupBy) return;
    this.selectedIds = [];
    this.groupedReports = [];
    this.ungroupedItems = [];
    this.groupBy = groupBy;

    this.pagination.resetPage();
  }

  @action.bound
  handleOrderChange(orderBy: {
    column: TimeTrackingItemOrderByColumnEnum;
    order: SortOrder | undefined;
  }): void {
    if (!orderBy.order) {
      this.orderBy.setClauses([]);
    } else {
      this.orderBy.setClauses([
        { column: orderBy.column, order: orderBy.order },
      ]);
    }

    this.pagination.resetPage();
  }

  @action.bound
  setSelectedIds(value: Store["selectedIds"]) {
    const ids = Array.from(new Set(value));
    this.selectedIds = ids;

    if (ids.length === 0) {
      this.selectedSummary = undefined;
      this.lockedSelectedIds = [];
      this.isSummaryLoading = false;
      this.idFilter?.setValue([], { skipOnChange: true });
      return;
    }

    const isSelectAll =
      ids.length > 0 &&
      this.summary?.ids !== undefined &&
      ids.length === this.summary.ids.length;
    this.idFilter?.setValue(isSelectAll ? [] : ids, { skipOnChange: true });

    this.fetchSelectedSummary();
    this.fetchLockedSelected();
  }

  @action.bound
  setSelectedSubtaskIds(value: string[]) {
    this.selectedSubtaskIds = Array.from(new Set(value));
  }

  @action
  setReports(
    data: TimeTrackingReportGroupedByQuery["timeTrackingReport"]["data"],
  ): void {
    this.groupedReports = data.map(
      ({ groupBy, trackedTime, items }, index) => ({
        id: String(index),
        groupBy: groupBy,
        trackedTime: trackedTime,
        __subRows: items,
      }),
    );
  }

  @action.bound
  async fetchReport(): Promise<void> {
    if (this.isGroupedByNone) return;

    const [data, error] = await this.reportFetcher.fetch({
      page: this.queryParams.page,
      first: this.queryParams.first,
      where: this.queryParams.filters.where,
      // NOTE: We can safely typecast this because we have groupBy is none guard condition
      groupBy: this.groupBy as TimeTrackingReportGroupBy,
      orderBy: this.queryParams.filters.orderBy,
    });

    if (error || !data?.timeTrackingReport) return;

    this.setReports(data.timeTrackingReport.data);
    this.pagination.setFromPaginatorInfo(data.timeTrackingReport.paginatorInfo);
  }

  @action.bound
  async fetchUngroupeditems(): Promise<void> {
    const [data, error] = await this.ungroupedItemsFetcher.fetch({
      page: this.queryParams.page,
      first: this.queryParams.first,
      from: toApiDate(this.dateRangeValue.start),
      to: toApiDate(this.dateRangeValue.end),
      where: this.queryParams.filters.where,
      orderBy: this.queryParams.filters.orderBy,
    });

    if (error || !data.getTimeTrackingItems) return;

    this.ungroupedItems = data.getTimeTrackingItems.data;
    this.pagination.setFromPaginatorInfo(
      data.getTimeTrackingItems.paginatorInfo,
    );
  }

  @action.bound
  async fetchSummary(): Promise<void> {
    const [data, error] = await this.summaryFetcher.fetch({
      where: this.queryParams.filters.where,
      from: toApiDate(this.dateRangeValue.start),
      to: toApiDate(this.dateRangeValue.end),
    });

    if (error || !data.getTimeTrackingItemsSummary) return;

    this.summary = data.getTimeTrackingItemsSummary;
  }

  @action.bound
  async fetchSelectedSummary(): Promise<void> {
    if (this.selectedIds.length === 0) return;

    this.isSummaryLoading = true;

    const idWhere = this.idFilter?.gqlWhere;
    const where = idWhere
      ? { AND: [...this.where.asGraphQueryParam.AND, idWhere] }
      : this.where.asGraphQueryParam;

    const [data, error] = await this.selectedSummaryFetcher.fetch({
      where,
      from: toApiDate(this.dateRangeValue.start),
      to: toApiDate(this.dateRangeValue.end),
    });

    if (!error && data?.getTimeTrackingItemsSummary) {
      this.selectedSummary = data.getTimeTrackingItemsSummary;
    }

    this.isSummaryLoading = false;
  }

  @action.bound
  async fetchLockedSelected(): Promise<void> {
    if (this.selectedIds.length === 0) return;

    const [data] = await this.checkLockedFetcher.fetch({
      itemIds: this.selectedIds,
    });

    this.lockedSelectedIds = data?.checkTimeTrackingItemsLocked ?? [];
  }

  private userSimpleWhere = new Filters<UsersWhereColumn>([
    new Filter({
      column: UsersWhereColumn.Scope,
      operator: SqlOperator.Eq,
      value: UserScopeEnum.Internal,
      options: [],
    }),
  ]);

  @action
  async fetchOptionsData() {
    const [data, error] = await this.optionsFetcher.fetch({
      userSimpleWhere: this.userSimpleWhere.asWhereParam,
    });

    if (error || !data) return;

    const userTeamId = this.appStore.authStore.user?.team?.id;
    const userId = this.appStore.authStore.user?.id;

    if (!userId || !userTeamId) return;

    let teamSimpleMap = clone(data.teamSimpleMap);
    let userSimpleMap = clone(data.userSimpleMap);

    switch (this.readPermission) {
      case "team":
        teamSimpleMap = teamSimpleMap.filter((t) => t.id === userTeamId);
        userSimpleMap = userSimpleMap.filter((u) => u.team_id === userTeamId);
        break;
      case "own":
        teamSimpleMap = teamSimpleMap.filter((t) => t.id === userTeamId);
        userSimpleMap = userSimpleMap.filter((u) => u.id === userId);
        break;
    }

    this.optionsData = {
      ...data,
      teamSimpleMap,
      userSimpleMap,
      tasksSimpleMap: this.optionsData?.tasksSimpleMap ?? undefined,
      subtasksSimpleMap: this.optionsData?.subtasksSimpleMap ?? undefined,
      ourWorkBudgetItemsSimpleMap:
        this.optionsData?.ourWorkBudgetItemsSimpleMap ?? undefined,
    };

    this.setFilterOptions(this.optionsData);
  }

  @action.bound
  setFilterOptions(options: Store["optionsData"]) {
    this.clientFilter?.setOptions(
      mapToOptions.toOptions(options?.clientsSimpleMap ?? [], "name"),
    );
    this.brandFilter?.setOptions(
      mapToOptions.clientBrands(options?.clientsSimpleMap ?? []),
    );
    this.projectFilter?.setOptions(
      mapToOptions.projects(options?.projectsSimpleMap ?? []),
    );
    this.workTypeFilter?.setOptions(
      mapToOptions.toOptions(options?.timeTrackingWorkTypes ?? [], "title"),
    );
    this.teamFilter?.setOptions(
      mapToOptions.toOptions(options?.teamSimpleMap ?? [], "name"),
    );
    this.projectCategoryFilter?.setOptions(
      options?.projectCategories?.map(({ id, name }) => ({
        label: name,
        value: id,
      })) ?? [],
    );
    this.filterUserOptions();
    this.filterProjectOptions();
  }

  @computed
  get dateFilter() {
    return this.where.filtersByColumn.get(
      TimeTrackingReportsWhereColumn.TrackedForDate,
    );
  }

  @computed
  get clientFilter() {
    return this.where.filtersByColumn.get(
      TimeTrackingReportsWhereColumn.ClientId,
    );
  }

  @computed
  get brandFilter() {
    return this.where.filtersByColumn.get(
      TimeTrackingReportsWhereColumn.BrandId,
    );
  }

  @computed
  get projectFilter() {
    return this.where.filtersByColumn.get(
      TimeTrackingReportsWhereColumn.ProjectId,
    );
  }

  @computed
  get owbiFilter() {
    return this.where.filtersByColumn.get(
      TimeTrackingReportsWhereColumn.OurWorkBudgetItemId,
    );
  }

  @computed
  get taskFilter() {
    return this.where.filtersByColumn.get(
      TimeTrackingReportsWhereColumn.TaskId,
    );
  }

  @computed
  get subtaskFilter(): Filter<TimeTrackingReportsWhereColumn> | undefined {
    return this.where.filtersByColumn.get(
      TIME_TRACKING_REPORT_SUBTASK_FILTER_COLUMN,
    );
  }

  @computed
  get workTypeFilter() {
    return this.where.filtersByColumn.get(
      TimeTrackingReportsWhereColumn.WorkTypeId,
    );
  }

  @computed
  get teamFilter() {
    return this.where.filtersByColumn.get(
      TimeTrackingReportsWhereColumn.TeamId,
    );
  }

  @computed
  get userFilter() {
    return this.where.filtersByColumn.get(
      TimeTrackingReportsWhereColumn.UserId,
    );
  }

  @computed
  get statusFilter() {
    return this.where.filtersByColumn.get(
      TimeTrackingReportsWhereColumn.Status,
    );
  }

  @computed
  get billableFilter() {
    return this.where.filtersByColumn.get(
      TimeTrackingReportsWhereColumn.Billable,
    );
  }

  @computed
  get projectCategoryFilter() {
    return this.where.filtersByColumn.get(
      TimeTrackingReportsWhereColumn.ProjectCategoryId,
    );
  }

  filterBrandOptions() {
    let filteredOptions = this.optionsData?.clientsSimpleMap ?? [];

    const selectedClientIds = this.clientFilter?.value;

    if (selectedClientIds?.length) {
      filteredOptions = filteredOptions.filter((c) =>
        selectedClientIds.includes(c.id),
      );
    }

    this.brandFilter?.setOptions(mapToOptions.clientBrands(filteredOptions));
    this.brandFilter?.sanitizeAvailableValue();
  }

  filterProjectOptions() {
    let filteredOptions = this.optionsData?.projectsSimpleMap ?? [];

    if (this.includeArchivedOptions !== true) {
      filteredOptions = filteredOptions.filter(
        (p) => !p.archived_at && !p.finished_at,
      );
    }

    let selectedBrandIds = this.brandFilter?.value;

    if (!selectedBrandIds?.length && this.clientFilter?.value.length) {
      selectedBrandIds = (this.optionsData?.clientsSimpleMap ?? []).flatMap(
        (c) =>
          this.clientFilter?.value.includes(c.id)
            ? c.brands.map((b) => b.id)
            : [],
      );
    }

    if (selectedBrandIds?.length) {
      filteredOptions = filteredOptions.filter((p) =>
        selectedBrandIds.includes(p.brand_id),
      );
    }

    this.projectFilter?.setOptions(mapToOptions.projects(filteredOptions));
    this.projectFilter?.sanitizeAvailableValue();
  }

  filterTaskOptions() {
    this.taskFilter?.sanitizeAvailableValue();
  }

  filterSubtaskOptions() {
    this.subtaskFilter?.sanitizeAvailableValue();
  }

  filterOwbiOptions() {
    this.owbiFilter?.sanitizeAvailableValue();
  }

  filterUserOptions() {
    let filteredOptions = this.optionsData?.userSimpleMap ?? [];

    if (this.includeArchivedOptions === false) {
      filteredOptions = filteredOptions.filter((u) => !u.archived_at);
    }

    const selectedTeamIds = this.teamFilter?.value;

    if (selectedTeamIds?.length) {
      filteredOptions = filteredOptions.filter(({ team_id }) => {
        if (!team_id) return false;
        return selectedTeamIds.includes(team_id);
      });
    }

    this.userFilter?.setOptions([
      ...mapToOptions.users(filteredOptions),
      {
        label: t`Work expense`,
        value: "work-expense",
      },
    ]);
    this.userFilter?.sanitizeAvailableValue({ skipOnChange: true });
  }

  @action.bound
  async fetchOWBIOptions() {
    const [data, error] = await this.owbiOptionsFetcher.fetch({
      project_id: this.projectFilter?.value ?? [],
    });

    if (!data?.ourWorkBudgetItemsSimpleMap || error) return;

    if (this.optionsData) {
      this.optionsData.ourWorkBudgetItemsSimpleMap =
        data.ourWorkBudgetItemsSimpleMap;
    }
    this.owbiFilter?.setOptions(
      mapToOptions.toOptions(data.ourWorkBudgetItemsSimpleMap, "title"),
    );
    this.filterOwbiOptions();
    if (!this.isInitialized) return;
    this.filterTaskOptions();
  }

  @action.bound
  async fetchTasksOptions() {
    const [data, error] = await this.tasksOptionsFetcher.fetch({
      our_work_budget_item_id: this.owbiFilter?.value ?? [],
    });

    if (!data?.tasksSimpleMap || error) return;
    const parentTasks = data.tasksSimpleMap.filter((task) => !task.parent);
    if (this.optionsData) {
      this.optionsData.tasksSimpleMap = parentTasks;
    }
    this.taskFilter?.setOptions(mapToOptions.toOptions(parentTasks, "name"));
    this.filterTaskOptions();
    await this.fetchSubtasksOptions();
  }

  @action.bound
  async fetchSubtasksOptions() {
    const [data, error] = await this.tasksOptionsFetcher.fetch({
      parent_ids: this.taskFilter?.value ?? [],
      our_work_budget_item_id: this.owbiFilter?.value ?? [],
    });

    if (!data?.tasksSimpleMap || error) return;
    if (this.optionsData) {
      this.optionsData.subtasksSimpleMap = data.tasksSimpleMap;
    }
    this.subtaskFilter?.setOptions(
      mapToOptions.toOptions(data.tasksSimpleMap, "name"),
    );
    this.filterSubtaskOptions();
  }

  @computed
  get isUnlockLoading(): boolean {
    return this.unlockMutator.isLoading.value;
  }

  @computed
  get isUpdateBillableLoading(): boolean {
    return this.updateBillableMutator.isLoading.value;
  }

  @computed
  get isLockCheckLoading(): boolean {
    return this.checkLockedFetcher.isLoading.value;
  }

  @action.bound
  async getUnlockedItemIds(ids: string[]): Promise<string[]> {
    if (ids.length === 0) return [];

    const [data] = await this.checkLockedFetcher.fetch({
      itemIds: ids,
    });

    const lockedIds = new Set(data?.checkTimeTrackingItemsLocked ?? []);

    if (lockedIds.size === 0) return ids;

    const unlockedIds = ids.filter((id) => !lockedIds.has(id));

    this.appStore.UIStore.toast({
      status: "info",
      title: t`${ids.length - unlockedIds.length} items are locked and changes won't be applied.`,
    });

    return unlockedIds;
  }

  @action.bound
  async updateTimeTrackingItemsBillable(
    billable: boolean,
    timeTrackingItemIds: string[],
  ): Promise<void> {
    await this.updateBillableMutator.mutate({
      billable,
      timeTrackingItemIds,
    });
    this.setSelectedIds([]);
    this.refetch();
  }

  @action.bound
  async unlockTimeTrackingItems(ids: string[]): Promise<void> {
    const [data, error] = await this.unlockMutator.mutate({ ids });

    if (error || !data?.unlockTimeTrackingItems) return;

    this.appStore.UIStore.toast({
      status: "success",
      title: `${t`Successfully unlocked`} ${data.unlockTimeTrackingItems.successful} ${t`time entries out of`} ${data.unlockTimeTrackingItems.total}`,
    });

    this.setSelectedIds([]);
    this.refetch();
  }

  @computed
  get isDeleteLoading(): boolean {
    return this.deleteMutator.isLoading.value;
  }

  @action.bound
  async deleteTimeTrackingItems(ids: string[]): Promise<void> {
    const [data, error] = await this.deleteMutator.mutate({ ids });

    if (error || !data?.bulkDeleteTimeTrackingItems) return;

    this.appStore.UIStore.toast({
      status: "success",
      title: `${t`Successfully deleted`} ${data.bulkDeleteTimeTrackingItems.successful} ${t`time entries out of`} ${data.bulkDeleteTimeTrackingItems.total}`,
    });

    this.setSelectedIds([]);
    this.refetch();
  }
}
